import { useState } from 'react';
import { api, ApiRequestError, useApi, type CreatorSummary } from '../api';
import { Card, ErrorBanner, Loading, haceCuanto } from '../components/ui';
import { useT, type Clave } from '../i18n';
import { useAviso } from '../avisos';

/**
 * Cuentas de referencia: perfiles ajenos que se siguen para inspirarse o comparar.
 *
 * ── Por qué los seguidores se cargan a mano ─────────────────────────────────────
 * Leerlos de una cuenta ajena tiene un único camino oficial —Business Discovery de la Graph
 * API— y exige Facebook Login: app de Meta revisada y Página vinculada. Este proyecto usa
 * Instagram Login justamente para no pedir eso. Raspar la página tampoco sirve: Instagram
 * responde con muro de sesión a las IP de centro de datos, y un Worker es exactamente eso.
 *
 * Se dice en la pantalla en vez de dejar una columna que a veces trae un número viejo y a
 * veces nada, sin que se sepa cuál de las dos cosas está pasando.
 */

type TrackedAccount = {
  id: string;
  /** `instagram` o `facebook`. Decide en qué pestaña aparece y qué se puede hacer con ella. */
  platform: string;
  username: string;
  url: string;
  followers: number | null;
  note: string | null;
  /** De qué es la cuenta. Se miran por motivos distintos y mezcladas hay que recordar cuál era cuál. */
  category: string;
  /** De qué país es, en texto libre. `null` es «no lo sé», que es un estado legítimo. */
  country: string | null;
  lastCheckedAt: string | null;
};

/**
 * Quién trabaja cada referente.
 *
 * ── Por qué vive acá y no en la ficha ──────────────────────────────────────────
 * Empezó como una pestaña dentro de cada influencer. La relación es de muchos a muchos —un
 * referente bueno lo trabajan varias— y desde la ficha, para saber quién trabaja una cuenta,
 * había que abrir todas las influencers y acordarse de lo visto. Acá se ve la grilla entera.
 *
 * Lo que se hace **en** Instagram —abrir el perfil, seguir, anotar la vuelta— no está en esta
 * pantalla ni en ninguna: ocurre del otro lado.
 */
type Asignacion = {
  id: string;
  trackedAccountId: string;
  creatorId: string;
  creatorName: string;
  lastWorkedAt: string | null;
  followedTotal: number;
};

/** Las categorías, con el nombre que se muestra. Una sola lista para el filtro y los selectores. */
const CATEGORIAS = [
  ['modelos', 'ref.catModelos'],
  ['meme', 'ref.catMeme'],
  ['comic', 'ref.catComic'],
] as const satisfies readonly (readonly [string, Clave])[];

/**
 * Compara texto ignorando mayúsculas y tildes.
 *
 * El país se escribe a mano, así que «Perú», «peru» y «PERU» son la misma respuesta. Un filtro
 * que las distinga obliga a recordar cómo se tipeó cada fila, que es exactamente lo que un
 * filtro tendría que evitar.
 */
function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function formatFollowers(value: number | null): string {
  if (value === null) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

export function Referencias() {
  const { t } = useT();
  const aviso = useAviso();
  const state = useApi<{ accounts: TrackedAccount[]; assignments: Asignacion[] }>(
    '/api/tracked-accounts',
  );
  /** Para el selector de «quién la trabaja». */
  const creators = useApi<{ creators: CreatorSummary[] }>('/api/creators');
  /** Qué fila tiene abierto el selector de influencer. Una por vez: veinte selectores no se leen. */
  const [asignandoEn, setAsignandoEn] = useState<string | null>(null);
  /** Qué asignación se está anotando a mano, y con qué valor. */
  const [anotando, setAnotando] = useState<{ id: string; value: string } | null>(null);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  /** Qué fila se está editando y con qué valor, para no re-renderizar la lista entera. */
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  /** Con qué categoría se agrega la próxima, y cuál se está viendo. */
  const [nuevaCategoria, setNuevaCategoria] = useState('modelos');
  const [filtro, setFiltro] = useState('todas');
  const [filtroPais, setFiltroPais] = useState('');
  /** Qué fila tiene el país abierto para editar. Separado del de seguidores: se editan aparte. */
  const [editandoPais, setEditandoPais] = useState<{ id: string; value: string } | null>(null);
  /**
   * Qué pestaña se está viendo.
   *
   * Las dos listas viven en la misma tabla y se separan acá porque se usan distinto: la de
   * Instagram es para ir cuenta por cuenta siguiendo gente, con asignación por influencer y
   * conteo diario. La de Facebook es una libreta de enlaces para abrir.
   */
  const [pestana, setPestana] = useState<'instagram' | 'facebook'>('instagram');
  const [nombreNuevo, setNombreNuevo] = useState('');

  if (state.loading && !state.data) return <Loading label={t('ref.cargando')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;

  // Las filas viejas no tienen plataforma guardada: son de cuando la lista era sólo Instagram.
  const todas = (state.data?.accounts ?? []).filter((a) => (a.platform || 'instagram') === pestana);
  const pais = normalizar(filtroPais);
  const accounts = todas
    .filter((a) => filtro === 'todas' || a.category === filtro)
    // Coincidencia parcial: escribir «arg» tiene que encontrar «Argentina». Buscar por igualdad
    // exacta convertiría el filtro en un acertijo sobre cómo se escribió la fila.
    .filter((a) => pais === '' || normalizar(a.country ?? '').includes(pais));

  /** Las asignaciones agrupadas por referente, que es como las dibuja la lista. */
  const porCuenta = new Map<string, Asignacion[]>();
  for (const asignacion of state.data?.assignments ?? []) {
    const existentes = porCuenta.get(asignacion.trackedAccountId) ?? [];
    existentes.push(asignacion);
    porCuenta.set(asignacion.trackedAccountId, existentes);
  }

  /**
   * A qué influencers todavía se les puede asignar esta cuenta.
   *
   * Las externas y las borradas no aparecen, pero eso no se filtra acá: `/api/creators` ya las
   * deja fuera —`kind != 'external'` y `deleted_at IS NULL`— y repetir el filtro en la pantalla
   * sería un segundo sitio que puede quedar diciendo otra cosa.
   */
  const sinAsignarA = (trackedAccountId: string): CreatorSummary[] => {
    const yaTiene = new Set(
      (porCuenta.get(trackedAccountId) ?? []).map((asignacion) => asignacion.creatorId),
    );
    return (creators.data?.creators ?? []).filter((creator) => !yaTiene.has(creator.id));
  };

  /**
   * Asigna esta cuenta a una influencer.
   *
   * La ruta cuelga de la influencer y no del referente porque el alcance por dueño se resuelve
   * subiendo a la influencer: `/api/creators/:id/referentes` ya está filtrada, y una ruta espejo
   * del lado del referente tendría que repetir esa comprobación con otra redacción.
   */
  const asignar = async (creatorId: string, trackedAccountId: string): Promise<void> => {
    setAsignandoEn(null);
    try {
      await api.post(`/api/creators/${creatorId}/referentes`, { trackedAccountId });
      await state.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ref.noSePudoAsignar'));
    }
  };

  /**
   * Anota una vuelta a mano sobre una asignación.
   *
   * Es la misma ruta que usa la extensión, y por eso las dos cifras conviven sin pisarse: cada
   * vuelta es una fila nueva, no un valor que se reemplaza. Anotar cinco a mano después de que la
   * extensión contó doce da diecisiete, que es lo correcto — la extensión no vio esos cinco.
   */
  const anotarVuelta = async (
    creatorId: string,
    referenteId: string,
    raw: string,
  ): Promise<void> => {
    setAnotando(null);

    const cantidad = Number(raw.trim());
    // Vacío o no numérico se descarta en silencio: es cómo se sale del campo sin querer anotar,
    // y un aviso de error ahí sería regañar por haber cambiado de idea.
    if (raw.trim() === '' || !Number.isFinite(cantidad) || cantidad < 0) return;

    try {
      await api.post(`/api/creators/${creatorId}/referentes/${referenteId}/vuelta`, {
        followedCount: cantidad,
      });
      await state.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ref.noSePudoAnotar'));
    }
  };

  const desasignar = async (creatorId: string, referenteId: string): Promise<void> => {
    await api.del(`/api/creators/${creatorId}/referentes/${referenteId}`);
    await state.reload();
  };

  const add = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.post('/api/tracked-accounts', {
        url: url.trim(),
        category: nuevaCategoria,
        platform: pestana,
        // Sólo en Facebook: en Instagram el nombre es el usuario, y sale del propio enlace.
        ...(pestana === 'facebook' && nombreNuevo.trim().length > 0
          ? { name: nombreNuevo.trim() }
          : {}),
      });
      setUrl('');
      setNombreNuevo('');
      await state.reload();
    } catch (caught) {
      if (!(caught instanceof ApiRequestError)) aviso.error(t('ref.noSePudoAgregar'));
    } finally {
      setBusy(false);
    }
  };

  const savePais = async (id: string, valor: string): Promise<void> => {
    setEditandoPais(null);
    await api.patch(`/api/tracked-accounts/${id}`, { country: valor.trim() });
    await state.reload();
  };

  const saveFollowers = async (id: string, raw: string): Promise<void> => {
    // Acepta «12.4K», «1,2M» y números con puntos: es como vienen copiados del perfil.
    const limpio = raw.trim().replace(/\s/g, '').toUpperCase();
    const multiplicador = limpio.endsWith('M') ? 1_000_000 : limpio.endsWith('K') ? 1_000 : 1;
    const numero = Number(limpio.replace(/[KM]$/, '').replace(/\./g, '').replace(',', '.'));

    setEditing(null);
    if (!Number.isFinite(numero)) return;

    await api.patch(`/api/tracked-accounts/${id}`, {
      followers: Math.round(numero * multiplicador),
    });
    await state.reload();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('ref.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
          {pestana === 'instagram'
            ? t('ref.bajadaIg')
            : t('ref.bajadaFb')}
        </p>
      </div>

      {/*
        Dos listas en la misma tabla, separadas por cómo se usan.

        La de Instagram sirve para ir cuenta por cuenta siguiendo gente: tiene asignación por
        influencer, conteo diario y seguidores. La de Facebook es una libreta de enlaces —una
        Página, un grupo, un negocio— que se abren y se miran. Mezclarlas obligaría a que cada
        fila explique cuál de las dos cosas es.
      */}
      <div className="flex gap-2">
        {(['instagram', 'facebook'] as const).map((cual) => (
          <button
            key={cual}
            type="button"
            onClick={() => setPestana(cual)}
            className={`rounded-xl border px-3 py-1.5 text-sm ${
              pestana === cual
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-[var(--color-border-subtle)] text-[var(--color-ink-muted)]'
            }`}
          >
            {cual === 'instagram' ? 'Instagram' : 'Facebook'}
          </button>
        ))}
      </div>

      <Card title={t('ref.agregarUna')}>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && url.trim().length > 0 && !busy) void add();
            }}
            placeholder={
              pestana === 'facebook'
                ? t('ref.placeholderFb')
                : t('ref.placeholderIg')
            }
            className="min-w-0 flex-1 rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-sm"
          />
          {/*
            El nombre, sólo en Facebook.

            En Instagram el nombre es el usuario y sale del propio enlace. Una dirección de
            Facebook puede no tener ninguno legible —profile.php?id=… es lo habitual— y una lista
            de enlaces sin nombre es una lista que hay que abrir entera para saber qué tiene.
          */}
          {pestana === 'facebook' && (
            <input
              value={nombreNuevo}
              onChange={(event) => setNombreNuevo(event.target.value)}
              placeholder={t('ref.nombreOpcional')}
              className="w-48 rounded-xl border border-[var(--color-border-subtle)] px-3 py-2 text-sm"
            />
          )}
          {/* De qué es, al agregarla: clasificarla después exige acordarse de por qué se guardó. */}
          <select
            value={nuevaCategoria}
            onChange={(event) => setNuevaCategoria(event.target.value)}
            className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-2 text-sm"
          >
            {CATEGORIAS.map(([valor, clave]) => (
              <option key={valor} value={valor}>
                {t(clave)}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={busy || url.trim().length === 0}
            onClick={() => void add()}
            className="rounded-xl bg-[var(--color-accent)] px-4 py-2 text-sm font-medium text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {t(busy ? 'ref.agregando' : 'ref.agregar')}
          </button>
        </div>

        <p className="mt-3 text-xs text-[var(--color-ink-muted)]">
          {t('ref.porUsuarioAntes')}
          <code className="font-mono">/chloe</code>,{' '}
          <code className="font-mono">/chloe/</code>
          {t('ref.porUsuarioY')}
          <code className="font-mono">/chloe?hl=es</code>
          {t('ref.porUsuarioFin')}
        </p>
      </Card>

      {/*
        La limitación, dicha una vez y donde importa.

        Prometer un número que no podemos leer sería peor que no tenerlo: una columna que a
        veces trae un dato viejo y a veces nada, sin forma de saber cuál de las dos.
      */}
      <div className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4 text-sm text-[var(--color-ink-muted)]">
        <strong className="text-[var(--color-ink)]">{t('ref.seguidoresAMano')}</strong>
        {t('ref.seguidoresAManoQue')}
      </div>

      <Card title={t('ref.cuentas', { n: accounts.length })}>
        {/*
          El filtro, con cuántas hay de cada una.

          El número al lado evita el caso peor de un filtro: elegir una categoría, ver la lista
          vacía y no saber si es que no hay ninguna o si algo falló.
        */}
        <div className="mb-3 flex flex-wrap gap-2">
          {([['todas', 'ref.catTodas'], ...CATEGORIAS] as const).map(([valor, clave]) => {
            const cuantas =
              valor === 'todas' ? todas.length : todas.filter((a) => a.category === valor).length;

            return (
              <button
                key={valor}
                type="button"
                onClick={() => setFiltro(valor)}
                className={
                  'rounded-xl border px-3 py-1.5 text-xs transition ' +
                  (filtro === valor
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] font-medium'
                    : 'border-[var(--color-border-subtle)] hover:border-[var(--color-border-strong)]')
                }
              >
                {t(clave)}{' '}
                <span className="tabular-nums text-[var(--color-ink-muted)]">{cuantas}</span>
              </button>
            );
          })}

          {/*
            El filtro de país es un campo y no una lista de fichas.

            Con las categorías —tres, fijas— las fichas funcionan. El país es texto libre y no se
            sabe cuántos valores va a haber: veinte fichas ocuparían más que la propia tabla.
          */}
          <input
            value={filtroPais}
            onChange={(event) => setFiltroPais(event.target.value)}
            placeholder={t('ref.filtrarPorPais')}
            aria-label={t('ref.filtrarPorPaisAria')}
            className="rounded-xl border border-[var(--color-border-subtle)] bg-transparent px-3 py-1.5 text-xs"
          />
          {filtroPais.trim() !== '' && (
            <button
              type="button"
              onClick={() => setFiltroPais('')}
              className="rounded-xl px-2 py-1.5 text-xs text-[var(--color-ink-muted)] underline underline-offset-2"
            >
              {t('ref.quitarFiltro')}
            </button>
          )}
        </div>

        {accounts.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">{t('ref.ningunaAun')}</p>
        ) : (
          <ul className="divide-y divide-[var(--color-border-subtle)]">
            {accounts.map((account) => (
              <li key={account.id} className="py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={account.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex-1 text-sm font-medium hover:text-[var(--color-accent)]"
                  >
                    @{account.username} ↗
                  </a>

                  {/*
                  La categoría, editable en la fila.

                  Se cambia acá y no en otra pantalla porque es donde se ve el error: al mirar la
                  lista uno reconoce que una cuenta está mal clasificada, y obligar a abrir algo
                  para arreglarlo hace que no se arregle.
                */}
                  <select
                    value={account.category}
                    onChange={(event) => {
                      void api
                        .patch(`/api/tracked-accounts/${account.id}`, {
                          category: event.target.value,
                        })
                        .then(() => state.reload());
                    }}
                    className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1 text-xs"
                  >
                    {CATEGORIAS.map(([valor, clave]) => (
                      <option key={valor} value={valor}>
                        {t(clave)}
                      </option>
                    ))}
                  </select>

                  {/*
                  El país, editable donde se lee.

                  Mismo criterio que la categoría: al mirar la lista uno reconoce que falta o que
                  está mal, y obligar a abrir otra pantalla para corregirlo hace que no se
                  corrija. Vacío muestra un guion, que se distingue de un país en blanco.
                */}
                  {editandoPais?.id === account.id ? (
                    <input
                      autoFocus
                      value={editandoPais.value}
                      onChange={(event) =>
                        setEditandoPais({ id: account.id, value: event.target.value })
                      }
                      onBlur={() => void savePais(account.id, editandoPais.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void savePais(account.id, editandoPais.value);
                        if (event.key === 'Escape') setEditandoPais(null);
                      }}
                      placeholder="Argentina"
                      className="w-32 rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-xs"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setEditandoPais({ id: account.id, value: account.country ?? '' })
                      }
                      className={
                        'w-32 truncate rounded-lg px-2 py-1 text-left text-xs transition hover:bg-[var(--color-surface-sunken)] ' +
                        (account.country ? '' : 'text-[var(--color-ink-faint)]')
                      }
                      title={t('ref.editarPais')}
                    >
                      {account.country ?? t('ref.sinPais')}
                    </button>
                  )}

                  {editing?.id === account.id ? (
                    <input
                      autoFocus
                      value={editing.value}
                      onChange={(event) =>
                        setEditing({ id: account.id, value: event.target.value })
                      }
                      onBlur={() => void saveFollowers(account.id, editing.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') void saveFollowers(account.id, editing.value);
                        if (event.key === 'Escape') setEditing(null);
                      }}
                      placeholder="12.4K"
                      className="w-28 rounded-lg border border-[var(--color-border-subtle)] px-2 py-1 text-right text-sm"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({
                          id: account.id,
                          value: account.followers === null ? '' : String(account.followers),
                        })
                      }
                      className="w-28 rounded-lg px-2 py-1 text-right text-sm tabular-nums transition hover:bg-[var(--color-surface-sunken)]"
                      title={t('ref.editar')}
                    >
                      {formatFollowers(account.followers)}
                      <span className="ml-1 text-xs text-[var(--color-ink-muted)]">
                        {t('ref.seg')}
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      void api.del(`/api/tracked-accounts/${account.id}`).then(() => state.reload())
                    }
                    className="rounded-lg px-2 py-1 text-xs text-[var(--color-ink-muted)] transition hover:text-[var(--color-danger)]"
                  >
                    {t('ref.quitar')}
                  </button>
                </div>

                {/*
                  Quién la trabaja, en su propia línea.

                  Metido en la fila de arriba desbordaba: esa línea ya lleva cinco controles, y
                  las influencers son un número que crece. Abajo, la fila se lee de un vistazo
                  —quién y hace cuánto— y sigue entrando en un teléfono.
                */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {(porCuenta.get(account.id) ?? []).map((asignacion) => (
                    <span
                      key={asignacion.id}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-surface-sunken)] px-2 py-1 text-xs"
                    >
                      <span className="font-medium">{asignacion.creatorName}</span>

                      {/*
                        Anotar la vuelta a mano, pulsando donde se lee.

                        Es la red de seguridad de la extensión: cuando Instagram cambie su HTML
                        —lo va a hacer— la detección automática se queda quieta, y sin esto se
                        pierde el registro hasta que alguien arregle el código. Con esto se sigue
                        trabajando igual, escribiendo el número.

                        Mismo gesto que el país y los seguidores de esta misma pantalla: se pulsa
                        el dato para editarlo. Un botón aparte multiplicaría los controles de una
                        ficha que ya es chica.
                      */}
                      {anotando?.id === asignacion.id ? (
                        <input
                          autoFocus
                          inputMode="numeric"
                          value={anotando.value}
                          onChange={(event) =>
                            setAnotando({ id: asignacion.id, value: event.target.value })
                          }
                          onBlur={() =>
                            void anotarVuelta(asignacion.creatorId, asignacion.id, anotando.value)
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              void anotarVuelta(
                                asignacion.creatorId,
                                asignacion.id,
                                anotando.value,
                              );
                            }
                            if (event.key === 'Escape') setAnotando(null);
                          }}
                          placeholder={t('ref.cuantos')}
                          aria-label={t('ref.aCuantosSiguio', {
                            quien: asignacion.creatorName,
                            cuenta: account.username,
                          })}
                          className="w-24 rounded border border-[var(--color-border-subtle)] bg-transparent px-1 py-0.5 text-right text-xs"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAnotando({ id: asignacion.id, value: '' })}
                          title={t('ref.anotarVuelta')}
                          className="text-[var(--color-ink-muted)] transition hover:text-[var(--color-ink)]"
                        >
                          {haceCuanto(asignacion.lastWorkedAt)}
                          {asignacion.followedTotal > 0 && ` · ${asignacion.followedTotal}`}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void desasignar(asignacion.creatorId, asignacion.id)}
                        aria-label={t('ref.quitarDe', {
                          quien: asignacion.creatorName,
                          cuenta: account.username,
                        })}
                        className="text-[var(--color-ink-faint)] transition hover:text-[var(--color-danger)]"
                      >
                        ×
                      </button>
                    </span>
                  ))}

                  {asignandoEn === account.id ? (
                    <select
                      autoFocus
                      defaultValue=""
                      onChange={(event) => {
                        if (event.target.value) void asignar(event.target.value, account.id);
                      }}
                      onBlur={() => setAsignandoEn(null)}
                      aria-label={t('ref.asignarA', { cuenta: account.username })}
                      className="rounded-lg border border-[var(--color-border-subtle)] bg-transparent px-2 py-1 text-xs"
                    >
                      <option value="">{t('ref.elegiInfluencer')}</option>
                      {sinAsignarA(account.id).map((creator) => (
                        <option key={creator.id} value={creator.id}>
                          {creator.publicName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    sinAsignarA(account.id).length > 0 && (
                      <button
                        type="button"
                        onClick={() => setAsignandoEn(account.id)}
                        className="rounded-lg border border-dashed border-[var(--color-border-subtle)] px-2 py-1 text-xs text-[var(--color-ink-muted)] transition hover:border-[var(--color-border-strong)] hover:text-[var(--color-ink)]"
                      >
                        {t('ref.masAsignar')}
                      </button>
                    )
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
